import { useState, useSyncExternalStore } from "react";
import { Compass } from "lucide-react";
import { readTourProgress, startTour } from "./HeaderTour";

// "Take the 3-minute tour" (D-O115): offered once to each person on their first sign-in after the role chapters arrived.
// "Later" puts it away; the tour stays in the account menu and in Go to. Not offered to automated browsers (the end-to-end
// suite), unless a test asks for it with the summitops.tour.offer.force flag.

export const OFFER_PREFIX = "summitops.tour.offered.v2";
export const OFFER_FORCE_KEY = "summitops.tour.offer.force";

export function offerKey(person: string | null | undefined): string {
  return `${OFFER_PREFIX}.${(person ?? "anonymous").toLowerCase()}`;
}

function automated(): boolean {
  try {
    if (window.localStorage.getItem(OFFER_FORCE_KEY) === "1") return false;
  } catch {
    // no storage: treat as a person
  }
  return (typeof navigator !== "undefined" && navigator.webdriver === true) || import.meta.env.MODE === "test";
}

/** Should the offer show for this person now? Not after it was answered, nor once they started or finished the tour. */
export function shouldOfferTour(person: string | null | undefined): boolean {
  if (!person || automated()) return false;
  try {
    if (window.localStorage.getItem(offerKey(person))) return false;
  } catch {
    return false;
  }
  const p = readTourProgress(person);
  return !p.done && p.step === null;
}

function markOffered(person: string) {
  try {
    window.localStorage.setItem(offerKey(person), new Date().toISOString());
  } catch {
    // The offer simply shows again next time.
  }
}

const noop = () => () => undefined;

export function TourOffer({ person, below = 56 }: { person: string | null | undefined; below?: number }) {
  const initial = useSyncExternalStore(noop, () => shouldOfferTour(person), () => false);
  const [dismissed, setDismissed] = useState(false);
  if (!person || !initial || dismissed) return null;

  const answer = (take: boolean) => {
    markOffered(person);
    setDismissed(true);
    if (take) startTour(person, true);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="tour-offer-title"
      className="fixed right-3 z-[85] w-80 max-w-[calc(100vw-1.5rem)]       rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-2xl print:hidden"
      style={{ top: below }}
      data-testid="tour-offer"
    >
      <h2 id="tour-offer-title" className="flex items-center gap-1.5 font-semibold text-slate-900">
        <Compass aria-hidden="true" className="h-4 w-4 text-brand-700" />
        Welcome to the pilot
      </h2>
      <p className="mt-1 leading-snug text-slate-700">A short tour shows you around Summit Operations, with a part for your own role.</p>
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <button type="button" className="btn-secondary !px-3 text-xs" onClick={() => answer(false)} data-testid="tour-offer-later">
          Later
        </button>
        <button type="button" autoFocus className="btn-primary !px-3 text-xs" onClick={() => answer(true)} data-testid="tour-offer-start">
          Take the 3-minute tour
        </button>
      </div>
    </div>
  );
}
